import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ChartContainer from '../common/ChartContainer';
import { CHART_TOOLTIP_CLASS } from '../common/chartTooltipClasses';
import PlayerControls from '../common/PlayerControls';
import {
  GateForceAllHiddenState,
  GateForceEmptyState,
  GateForceErrorState,
  GateForceNotice,
} from './GateForceChartStates';
import { GateForceHeaderControls, GateForceRowerLegend } from './GateForceDisplayControls';
import { useGateForcePlayback } from './useGateForcePlayback';
import { useGateForceViewModel } from './useGateForceViewModel';
import { buildDisplayInfo } from './utils/display';
import { GateForceChartRenderer } from './renderer';
import { useGateForceData } from '../../../../hooks/useGateForceData';
import { useErrorTracking } from '../../../../hooks/useErrorTracking';
import { useTheme } from '../../../../contexts/ThemeContext';
import {
  hullSetFilterSubtitle,
  useHullStabilityFilterOptional,
} from '../../../../contexts/HullStabilityFilterContext';
import type { ChartProps } from '../../../../utils/chartPropsPattern';
import type { RowerContext } from '../../../../types/rowerContext';
import { DEFAULT_CHART_WIDTH, DEFAULT_CHART_HEIGHT } from './utils/constants';

interface GateForceSignatureD3Props extends ChartProps {
  rowerContext?: RowerContext | null;
}

const GateForceSignatureD3: React.FC<GateForceSignatureD3Props> = ({
  sessionId,
  parameterInfo,
  className,
  rowerContext,
}) => {
  const { reportError } = useErrorTracking('GateForceSignatureD3');
  const { resolved: resolvedTheme } = useTheme();
  const hullFilter = useHullStabilityFilterOptional();
  const clipPathId = useId();
  const rendererRef = useRef(new GateForceChartRenderer());
  const containerRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isHoveredRef = useRef(false);

  const {
    loading,
    error,
    retry,
    rowerNames,
    strokesByRower,
    profilesByRower,
    skippedStrokesByRower,
  } = useGateForceData(sessionId, parameterInfo, rowerContext);

  const rowersWithData = useMemo(
    () => rowerNames.filter((rower) => (strokesByRower[rower] || []).length > 0),
    [rowerNames, strokesByRower]
  );

  // Global extents computed from ALL rowers and ALL strokes — not filtered by currentRower.
  // Passed to the renderer so the axis domain stays stable when switching rowers or modes.
  const globalAngleExtent = useMemo<[number, number] | undefined>(() => {
    let min = Infinity;
    let max = -Infinity;
    Object.values(strokesByRower).forEach((strokes) =>
      strokes?.forEach((stroke) =>
        stroke.forEach((p) => {
          if (p.x < min) min = p.x;
          if (p.x > max) max = p.x;
        })
      )
    );
    Object.values(profilesByRower).forEach((profile) =>
      profile?.forEach((p) => {
        if (p.x < min) min = p.x;
        if (p.x > max) max = p.x;
      })
    );
    return Number.isFinite(min) ? [min, max] : undefined;
  }, [strokesByRower, profilesByRower]);

  const globalForceExtent = useMemo<[number, number] | undefined>(() => {
    let min = Infinity;
    let max = -Infinity;
    Object.values(strokesByRower).forEach((strokes) =>
      strokes?.forEach((stroke) =>
        stroke.forEach((p) => {
          if (p.y < min) min = p.y;
          if (p.y > max) max = p.y;
        })
      )
    );
    Object.values(profilesByRower).forEach((profile) =>
      profile?.forEach((p) => {
        if (p.y < min) min = p.y;
        if (p.y > max) max = p.y;
      })
    );
    return Number.isFinite(min) ? [min, max] : undefined;
  }, [strokesByRower, profilesByRower]);

  const {
    availableRowers,
    viewMode,
    currentRower,
    showStrokeOverlay,
    selectedCrewRowers,
    hiddenRowers,
    setCurrentRower,
    tracesActive,
    averageActive,
    crewActive,
    isFilterMode,
    hasRowerContext,
    focusedRowerKey,
    handleTracesToggle,
    handleAverageToggle,
    handleCrewToggle,
    handleCrewRowerToggle,
    handleResetFilters,
    handleLegendToggle: _handleLegendToggle,
  } = useGateForceViewModel({ rowerNames, rowersWithData, rowerContext });

  const displayInfo = useMemo(
    () =>
      buildDisplayInfo({
        rowerNames,
        availableRowers,
        viewMode,
        currentRower,
        strokesByRower,
        profilesByRower,
        showStrokeOverlay,
        selectedCrewRowers,
      }),
    [
      rowerNames,
      availableRowers,
      viewMode,
      currentRower,
      strokesByRower,
      profilesByRower,
      showStrokeOverlay,
      selectedCrewRowers,
    ]
  );

  const visibleStrokeLines = useMemo(
    () => displayInfo.strokeLines.filter((line) => !hiddenRowers.includes(line.rower)),
    [displayInfo.strokeLines, hiddenRowers]
  );
  const visibleProfileLines = useMemo(
    () => displayInfo.profileLines.filter((line) => !hiddenRowers.includes(line.rower)),
    [displayInfo.profileLines, hiddenRowers]
  );

  const strokeLineCount = visibleStrokeLines.length;
  const hasVisibleLines = strokeLineCount > 0 || visibleProfileLines.length > 0;
  const allRowersHidden =
    (displayInfo.strokeLines.length > 0 || displayInfo.profileLines.length > 0) && !hasVisibleLines;

  const legendRowers = useMemo(() => {
    const unique = new Set<string>();
    [...displayInfo.strokeLines, ...displayInfo.profileLines].forEach((l) => unique.add(l.rower));
    return Array.from(unique);
  }, [displayInfo.strokeLines, displayInfo.profileLines]);

  const showPlaybackControls = rowersWithData.length > 0;

  const strokeCyclesForTrack = useMemo(() => {
    if (strokeLineCount === 0) return [];
    return Array.from({ length: strokeLineCount }, (_, i) => ({
      start: (i / strokeLineCount) * 100,
      end: ((i + 1) / strokeLineCount) * 100,
      strokeNumber: i + 1,
    }));
  }, [strokeLineCount]);

  const {
    isPlaying,
    progress,
    trackProgress,
    speed,
    prefersReducedMotion,
    handlePlayPause,
    handleReset,
    handleSpeedChange,
    handleProgressChange,
    handleScrubDirection,
    hasInteracted,
  } = useGateForcePlayback({ strokeLineCount });

  // Auto-enable traces when a playback action is triggered from profile-only mode
  const ensureTraces = useCallback(() => {
    if (!tracesActive && rowersWithData.length > 0) handleTracesToggle();
  }, [tracesActive, rowersWithData.length, handleTracesToggle]);

  const handlePlayPauseWithTraces = useCallback(() => {
    ensureTraces();
    handlePlayPause();
  }, [ensureTraces, handlePlayPause]);

  const handleResetWithTraces = useCallback(() => {
    ensureTraces();
    handleReset();
  }, [ensureTraces, handleReset]);

  // --- Inline ResizeObserver (handles conditionally-rendered wrapper) ---
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const chartWrapperCallbackRef = useCallback((node: HTMLDivElement | null) => {
    (chartWrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, []);

  useEffect(() => {
    const element = chartWrapperRef.current;
    if (!element) return;

    let raf: number | null = null;
    const commit = (w: number, h: number) => {
      setDimensions((prev) => {
        const rw = Math.round(w);
        const rh = Math.round(h);
        if ((rw === 0 && rh === 0) || (prev.width === rw && prev.height === rh)) return prev;
        return { width: rw, height: rh };
      });
    };

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => commit(entry.contentRect.width, entry.contentRect.height));
    });

    ro.observe(element);
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) commit(rect.width, rect.height);

    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [loading, error]);

  const width = dimensions.width || DEFAULT_CHART_WIDTH;
  const height = dimensions.height || DEFAULT_CHART_HEIGHT;

  const progressRef = useRef(progress);
  progressRef.current = progress;

  const chartReady = !loading && !error && hasVisibleLines;

  // Full chart render — fires on data/config/resize changes, NOT on progress
  useEffect(() => {
    if (!svgRef.current || !chartReady) return;
    rendererRef.current.render({
      svgElement: svgRef.current,
      containerElement: chartWrapperRef.current,
      tooltipElement: tooltipRef.current,
      clipPathId,
      width,
      height,
      visibleStrokeLines,
      visibleProfileLines,
      globalAngleExtent,
      globalForceExtent,
      legendRowers,
      viewMode,
      progress: hasInteracted ? progressRef.current : 100,
      focusedRowerKey,
    });
  }, [
    width,
    height,
    visibleStrokeLines,
    visibleProfileLines,
    globalAngleExtent,
    globalForceExtent,
    legendRowers,
    clipPathId,
    viewMode,
    chartReady,
    hasInteracted,
    focusedRowerKey,
    resolvedTheme,
  ]);

  // Stable ref for keyboard handler state — avoids re-registering the listener on every change
  const keyboardStateRef = useRef({
    showPlaybackControls,
    prefersReducedMotion,
    handlePlayPause: handlePlayPauseWithTraces,
    handleReset: handleResetWithTraces,
    handleScrubDirection,
  });
  keyboardStateRef.current = {
    showPlaybackControls,
    prefersReducedMotion,
    handlePlayPause: handlePlayPauseWithTraces,
    handleReset: handleResetWithTraces,
    handleScrubDirection,
  };

  // Document-level keyboard shortcuts, scoped to this chart via hover detection
  useEffect(() => {
    if (!chartReady) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isHoveredRef.current) return;
      const state = keyboardStateRef.current;
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      )
        return;
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        if (state.showPlaybackControls) state.handlePlayPause();
        return;
      }
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        rendererRef.current.resetZoom(!state.prefersReducedMotion);
        state.handleReset();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        state.handleScrubDirection(e.key === 'ArrowRight' ? 1 : -1);
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [chartReady]);

  // Lightweight stroke-reveal update (hot path during animation).
  // Before the user interacts, show all strokes (idle preview) by passing progress=100.
  useEffect(() => {
    const effectiveProgress = hasInteracted ? progress : 100;
    rendererRef.current.updateProgress(effectiveProgress, isPlaying);
  }, [progress, isPlaying, hasInteracted]);

  useEffect(() => {
    const renderer = rendererRef.current;
    return () => renderer.cleanup();
  }, []);

  useEffect(() => {
    if (error) {
      reportError(new Error(error), {
        context: 'gate_force_signature_d3',
        sessionId: sessionId || 'unknown',
      });
    }
  }, [error, sessionId, reportError]);

  const handleResetZoom = () => {
    rendererRef.current.resetZoom(!prefersReducedMotion);
  };

  const showOverlay = loading || !!error || allRowersHidden || !hasVisibleLines;

  const headerControls = chartReady ? (
    <GateForceHeaderControls
      tracesActive={tracesActive}
      averageActive={averageActive}
      crewActive={crewActive}
      availableRowers={availableRowers}
      currentRower={currentRower}
      hiddenRowersCount={hiddenRowers.length}
      hasRowerContext={hasRowerContext}
      isFilterMode={isFilterMode}
      onTracesToggle={handleTracesToggle}
      onAverageToggle={handleAverageToggle}
      onCrewToggle={handleCrewToggle}
      onCurrentRowerChange={setCurrentRower}
      onResetFilters={handleResetFilters}
    />
  ) : null;

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
      className="h-full w-full"
    >
      <ChartContainer
        title="Gate Force Signature"
        subtitle={hullSetFilterSubtitle('Gate force (kgf) versus gate angle (°).', hullFilter)}
        maximizable
        loading={loading}
        controls={headerControls}
        className={className}
      >
        <div className="flex flex-col h-full gap-4">
          {!loading && !error && chartReady && (
            <>
              {displayInfo.notice && <GateForceNotice message={displayInfo.notice} />}

              <div className="min-h-[142px]">
                {showPlaybackControls && (
                  <PlayerControls
                    isPlaying={isPlaying}
                    progress={trackProgress}
                    speed={speed}
                    hasData={strokeLineCount > 0}
                    windowRangeLabel=""
                    onPlayPause={handlePlayPauseWithTraces}
                    onReset={handleResetWithTraces}
                    onSpeedChange={handleSpeedChange}
                    onProgressChange={handleProgressChange}
                    onResetZoom={handleResetZoom}
                    allStrokeCycles={strokeCyclesForTrack}
                    timeExtent={[0, 100]}
                    animationRange={100}
                  />
                )}
              </div>
            </>
          )}

          {/* Chart wrapper always rendered so ResizeObserver can measure it */}
          <div
            ref={chartWrapperCallbackRef}
            className="relative w-full h-[400px] sm:h-[500px] lg:h-[680px] overflow-hidden mx-auto"
            style={{ maxWidth: '1024px' }}
          >
            {!loading && error && <GateForceErrorState onRetry={retry} />}
            {!loading && !error && allRowersHidden && (
              <GateForceAllHiddenState onResetFilters={handleResetFilters} />
            )}
            {!loading && !error && !allRowersHidden && !hasVisibleLines && (
              <GateForceEmptyState message={displayInfo.emptyMessage} />
            )}

            <svg
              ref={svgRef}
              className="w-full h-full"
              style={{ display: showOverlay ? 'none' : undefined }}
            />
            <div
              ref={tooltipRef}
              className={CHART_TOOLTIP_CLASS}
              style={{ opacity: 0, display: showOverlay ? 'none' : undefined }}
            />
          </div>

          {/* Rower legend — below chart, matching Power Analysis */}
          {chartReady && (
            <GateForceRowerLegend
              availableRowers={availableRowers}
              selectedCrewRowers={selectedCrewRowers}
              crewActive={crewActive}
              focusedRowerKey={focusedRowerKey}
              rowerContext={rowerContext}
              onCrewRowerToggle={handleCrewRowerToggle}
            />
          )}

          {chartReady && viewMode !== 'crew-profiles' && skippedStrokesByRower[currentRower] ? (
            <GateForceNotice
              message={`${skippedStrokesByRower[currentRower]} partial strokes omitted due to incomplete data.`}
            />
          ) : null}

          {chartReady &&
          viewMode === 'crew-profiles' &&
          selectedCrewRowers.length > 0 &&
          selectedCrewRowers.some((rower) => skippedStrokesByRower[rower]) ? (
            <GateForceNotice message="Some session averages omit strokes with incomplete samples." />
          ) : null}
        </div>
      </ChartContainer>
    </div>
  );
};

export default GateForceSignatureD3;
